"use client";

import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, TABLE, TD, TH, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  DiagramDownload,
  useDiagramRef,
} from "@/components/tools/geometry/diagram";
import { PolygonAngleDiagram } from "@/components/tools/geometry/polygon-angle-diagram";
import type { POLYGON_ANGLE_CALCULATOR_STRINGS } from "@/components/tools/polygon-angle-calculator";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  type AngleInputUnit,
  parseAngleInput,
} from "@/lib/tool-kit/geometry/angle-input";
import { formatExact } from "@/lib/tool-kit/geometry/exact";
import {
  type AngleKind,
  missingAngle,
  type PolygonFacts,
  parseAngleList,
  polygonFacts,
  radiansExact,
  sidesFromAngle,
  validSides,
} from "@/lib/tool-kit/geometry/polygon-angles";
import {
  formatRationalNice,
  type Rational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

type Strings = typeof POLYGON_ANGLE_CALCULATOR_STRINGS;
type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

export function nameOf(strings: Strings, sides: number): string {
  const names = strings.names as Record<number, string>;
  return names[sides] ?? t(strings.genericName, { n: sides });
}

/** "108", or "900/7 ≈ 128.5714" when the exact value is a fraction. */
function degreeText(value: Rational, mark: "." | ",", fmt: Format): string {
  const nice = formatRationalNice(value, mark);
  return nice.includes("/")
    ? `${nice} ≈ ${fmt(toNumberR(value), { maximumFractionDigits: 4 })}`
    : nice;
}

function radianText(value: Rational, fmt: Format): string {
  const exact = formatExact(radiansExact(value));
  return `${exact} ≈ ${fmt(toNumberR(value) * (Math.PI / 180), { maximumFractionDigits: 4 })}`;
}

function workingFor(facts: PolygonFacts, mark: "." | ","): string[] {
  const n = facts.sides;
  const shown = (value: Rational) => formatRationalNice(value, mark);
  return [
    `Σ interior = (n − 2) × 180° = (${n} − 2) × 180° = ${shown(facts.interiorSum)}°`,
    `Each interior = (n − 2) × 180° / n = ${shown(facts.interiorSum)}° / ${n} = ${shown(facts.interiorEach)}°`,
    `Each exterior = 360° / n = 360° / ${n} = ${shown(facts.exteriorEach)}°`,
    `Interior + exterior = ${shown(facts.interiorEach)}° + ${shown(facts.exteriorEach)}° = 180°`,
    `Diagonals = n(n − 3) / 2 = ${n} × ${n - 3} / 2 = ${facts.diagonals}`,
  ];
}

function FactsView({
  facts,
  strings,
  extra,
  mark,
  fmt,
}: {
  facts: PolygonFacts;
  strings: Strings;
  extra: string[];
  mark: "." | ",";
  fmt: Format;
}) {
  const svgRef = useDiagramRef();
  const name = nameOf(strings, facts.sides);
  const rows: { label: string; value: Rational }[] = [
    { label: strings.rows.interiorSum, value: facts.interiorSum },
    { label: strings.rows.interiorEach, value: facts.interiorEach },
    { label: strings.rows.exteriorEach, value: facts.exteriorEach },
    { label: strings.rows.exteriorSum, value: facts.exteriorSum },
    { label: strings.rows.central, value: facts.central },
  ];
  const summary = t(strings.summarySides, {
    name,
    sum: formatRationalNice(facts.interiorSum, mark),
    each: formatRationalNice(facts.interiorEach, mark),
    ext: formatRationalNice(facts.exteriorEach, mark),
  });
  const degrees = (value: Rational) =>
    `${fmt(toNumberR(value), { maximumFractionDigits: 4 })}°`;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.rows.interiorEach}
          value={`${degreeText(facts.interiorEach, mark, fmt)}°`}
        />
        <StatCard
          label={strings.rows.exteriorEach}
          value={`${degreeText(facts.exteriorEach, mark, fmt)}°`}
        />
        <StatCard label={strings.diagonals} value={fmt(facts.diagonals)} />
      </StatGrid>
      <div className={`${CARD} overflow-x-auto`}>
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.measurementHeader}
              </th>
              <th className={TH} scope="col">
                {strings.degreesHeader}
              </th>
              <th className={TH} scope="col">
                {strings.radiansHeader}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <th className={`${TD} font-normal`} scope="row">
                  {row.label}
                </th>
                <td className="border-border border-b px-3 py-2 font-mono text-foreground">
                  {degreeText(row.value, mark, fmt)}°
                </td>
                <td className="border-border border-b px-3 py-2 font-mono text-foreground">
                  {radianText(row.value, fmt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summary}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        {[...extra, ...workingFor(facts, mark)].map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
      <h3 className={WIDGET_H3}>{strings.figureHeading}</h3>
      <div className="space-y-2">
        <PolygonAngleDiagram
          labels={{
            interior: `α = ${degrees(facts.interiorEach)}`,
            exterior: `β = ${degrees(facts.exteriorEach)}`,
            central: `γ = ${degrees(facts.central)}`,
          }}
          sides={facts.sides}
          svgRef={svgRef}
          title={t(strings.figureTitle, { name })}
        />
        <p className="text-muted-foreground text-xs">{strings.figureKey}</p>
        <DiagramDownload
          fileName={`polygon-angles-${facts.sides}`}
          label={strings.downloadFigure}
          svgRef={svgRef}
        />
      </div>
    </>
  );
}

export function SidesOutcome({
  text,
  strings,
  mark,
  fmt,
}: {
  text: string;
  strings: Strings;
  mark: "." | ",";
  fmt: Format;
}) {
  if (text.trim() === "") {
    return <Notice>{strings.enter}</Notice>;
  }
  const sides = parseIntegerInput(text);
  if (sides === null || !validSides(sides)) {
    return <ErrorNotice>{strings.invalidSides}</ErrorNotice>;
  }
  return (
    <FactsView
      extra={[]}
      facts={polygonFacts(sides)}
      fmt={fmt}
      mark={mark}
      strings={strings}
    />
  );
}

export function AngleOutcome({
  kind,
  text,
  unit,
  strings,
  mark,
  fmt,
}: {
  kind: AngleKind;
  text: string;
  unit: AngleInputUnit;
  strings: Strings;
  mark: "." | ",";
  fmt: Format;
}) {
  if (text.trim() === "") {
    return <Notice>{strings.enter}</Notice>;
  }
  const angle = parseAngleInput(text, unit);
  if (angle === null) {
    return <ErrorNotice>{strings.invalidAngle}</ErrorNotice>;
  }
  if (angle.degrees === null) {
    return <ErrorNotice>{strings.range}</ErrorNotice>;
  }
  const found = sidesFromAngle(kind, angle.degrees);
  if (!found.ok) {
    return <ErrorNotice>{strings.range}</ErrorNotice>;
  }
  if (!found.whole) {
    return (
      <ErrorNotice>
        {t(strings.notWhole, { n: degreeText(found.sides, mark, fmt) })}
      </ErrorNotice>
    );
  }
  const sides = Number(found.sides.num);
  const shown = formatRationalNice(angle.degrees, mark);
  const line =
    kind === "interior"
      ? `n = 360° / (180° − interior) = 360° / (180° − ${shown}°) = ${sides}`
      : `n = 360° / exterior = 360° / ${shown}° = ${sides}`;
  return (
    <>
      <p className="text-foreground text-sm">
        {t(strings.polygonIs, { name: nameOf(strings, sides), n: sides })}
      </p>
      <FactsView
        extra={[line]}
        facts={polygonFacts(sides)}
        fmt={fmt}
        mark={mark}
        strings={strings}
      />
    </>
  );
}

export function MissingOutcome({
  sidesText,
  anglesText,
  strings,
  mark,
  fmt,
}: {
  sidesText: string;
  anglesText: string;
  strings: Strings;
  mark: "." | ",";
  fmt: Format;
}) {
  const sides = parseIntegerInput(sidesText);
  if (sides === null || !validSides(sides)) {
    return <ErrorNotice>{strings.invalidSides}</ErrorNotice>;
  }
  if (anglesText.trim() === "") {
    return <Notice>{strings.enterAngles}</Notice>;
  }
  const known = parseAngleList(anglesText);
  if (known === null) {
    return <ErrorNotice>{strings.invalidList}</ErrorNotice>;
  }
  const result = missingAngle(sides, known);
  if (!result.ok) {
    const messages = {
      count: t(strings.countError, {
        n: sides,
        need: sides - 1,
        have: known.length,
      }),
      range: strings.rangeError,
      total: strings.totalError,
    };
    return <ErrorNotice>{messages[result.error]}</ErrorNotice>;
  }
  const shown = (value: Rational) => formatRationalNice(value, mark);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.missingResult}
          value={`${degreeText(result.missing, mark, fmt)}°`}
        />
        <StatCard
          label={t(strings.missingTotal, { n: sides })}
          value={`${shown(result.total)}°`}
        />
        <StatCard
          label={strings.missingKnown}
          value={`${shown(result.knownSum)}°`}
        />
      </StatGrid>
      {result.reflex ? <Notice>{strings.reflex}</Notice> : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={t(strings.summaryMissing, { missing: shown(result.missing) })}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        <p>{`Σ interior = (n − 2) × 180° = (${sides} − 2) × 180° = ${shown(result.total)}°`}</p>
        <p>{`Σ known = ${known.map(shown).join(" + ")} = ${shown(result.knownSum)}°`}</p>
        <p>{`Missing = ${shown(result.total)}° − ${shown(result.knownSum)}° = ${shown(result.missing)}°`}</p>
      </div>
    </>
  );
}
